import {
  useCurrentUploadId,
  useCurrentUploadQuery,
  useUploadValidateDocument,
} from "@/hooks/uploads";
import Hyperlink from "@ingka/hyperlink";
import Loading from "@ingka/loading";
import SSRIcon from "@ingka/ssr-icon";
import noticeRedSmall from "@ingka/ssr-icon/paths/notice-red-small";
import Text from "@ingka/text";
import { useShallow } from "zustand/react/shallow";
import { NavigationButtons } from "../NavigationButtons/NavigationButtons";
import { useStore } from "@/services/store";
import Preview from "./components/document/Preview";
import Fields from "./components/document/Fields";
import Confirmation from "./components/document/Confirmation";
import { useMetadataQuery } from "@/hooks/metadata";
import { useMandatoryMediaNavigation } from "./hooks";
import { useAuth } from "@/hooks/auth";
import {
  isLockedForOwner as isLockedForOwnerRule,
  isUploadFinalized,
} from "./formFieldsAccessRules";
import { toRouteError } from "@/errors/routeError";

const MandatoryDocumentMetadata = () => {
  const currentUploadQuery = useCurrentUploadQuery();
  const metadataQuery = useMetadataQuery();
  const uploadId = useCurrentUploadId();
  const {
    user: { isAdmin },
  } = useAuth();
  const { refetch: validateDocument } = useUploadValidateDocument(uploadId);
  const storeAssets = useStore(useShallow((state) => state.documentAssets));

  const isLockedForOwner =
    currentUploadQuery.isSuccess &&
    (isLockedForOwnerRule({
      isAdmin,
      uploadState: currentUploadQuery.data.state,
    }) ||
      isUploadFinalized({ uploadState: currentUploadQuery.data.state }));
  const { onPrev, onContinue } = useMandatoryMediaNavigation(
    validateDocument,
    isLockedForOwner,
  );

  if (metadataQuery.isError || currentUploadQuery.isError) {
    throw toRouteError(metadataQuery.error ?? currentUploadQuery.error, {
      scope: "metadata",
      uploadId,
    });
  }

  if (!currentUploadQuery.isSuccess || !metadataQuery.isSuccess) {
    return <Loading text="Loading current upload request data" />;
  }

  const {
    bynder: { Language },
    documentContentTypes,
  } = metadataQuery.data;

  const { allOriginalConfirmation } = currentUploadQuery.data;

  const hasValidationErrors = Object.values(storeAssets).some(
    (asset) => asset?.validationMessage?.length > 0,
  );

  return (
    <div className="mx-space100 mt-space300 flex flex-col">
      <Text tagName="h2" headingSize="xs" className="mb-space100">
        Accessibility requirements for document uploads
      </Text>
      <Text tagName="p" bodySize="s">
        Before uploading a document to Ingka DAM, please ensure that the
        accessibility requirements for documents have been adhered to. To be
        able to continue to the next step you need to confirm that you have
        followed the requirements defined by accessibility hub for accessible
        documents. You can also use this as a checklist. Please visit{" "}
        <Hyperlink
          url="https://www.ikea.net/en/general/accessibility-hub/"
          text="Accessibility hub"
          newWindow
        />
        &apos;s website and take part in the trainings in My learning to keep up to
        date on how to create and export accessible documents.
      </Text>
      <div className="mt-space250 flex flex-col gap-y-space200">
        {Object.entries(storeAssets).map(([id, { filename, extension }]) => {
          const assetId = Number(id);
          return (
            <div
              className="flex gap-x-space150 pb-space200 pt-space200"
              key={assetId}
            >
              <div className="w-[18.5rem] shrink-0">
                <Preview
                  assetId={assetId}
                  fileName={filename}
                  extension={extension}
                />
              </div>
              <div className="w-[17.25rem] shrink-0">
                <Fields
                  assetId={assetId}
                  language={Language}
                  documentContentTypes={documentContentTypes}
                  isLockedForOwner={isLockedForOwner}
                />
              </div>
              <div className="flex-1">
                <Confirmation
                  assetId={assetId}
                  allOriginalConfirmation={allOriginalConfirmation}
                  isLockedForOwner={isLockedForOwner}
                />
              </div>
            </div>
          );
        })}
      </div>
      <div className="mb-space250 mt-space250 flex flex-row items-center justify-end align-middle">
        {hasValidationErrors && (
          <div className="mr-space100 flex flex-row items-center">
            <SSRIcon
              aria-hidden
              paths={noticeRedSmall}
              className="mt-[0.3rem]"
            />
            <Text tagName="span" className="text-semantic-negative">
              Required input missing
            </Text>
          </div>
        )}
        <NavigationButtons
          onPrev={onPrev}
          disabledNext={hasValidationErrors}
          onContinue={onContinue}
        />
      </div>
    </div>
  );
};

export default MandatoryDocumentMetadata;
